import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { OutputFormulaService } from 'src/app/services/output-formulas/output-formula.service';
import { OutputFormula } from 'src/app/models/output-formulas/output-formula';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';
import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { OutputFormulaValue } from 'src/app/models/output-formulas/output-formula-value';
import { filterRangeConventions } from './filename-convention-filters';
import { filterStillsSpecs, filterVideosSpecs } from './file-specification-filters';

export interface OutputFormulaRangeCreateDialogData {
  organizationId: string;
  fileSpecifications: FileSpecification[];
  filenameConventions: FilenameConvention[];
}

@Component({
  selector: 'app-output-formula-range-create-dialog',
  templateUrl: './output-formula-range-create-dialog.component.html',
  styleUrls: ['./output-formula-range-create-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class OutputFormulaRangeCreateDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(1)],
  });

  readonly stillsSpecIdControl = new FormControl<string | null>(null);
  readonly stillsConventionIdControl = new FormControl<string | null>(null);
  readonly videosSpecIdControl = new FormControl<string | null>(null);
  readonly videosConventionIdControl = new FormControl<string | null>(null);

  stillsSectionOpen = false;
  videosSectionOpen = false;
  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OutputFormulaRangeCreateDialogData,
    private readonly dialogRef: MatDialogRef<
      OutputFormulaRangeCreateDialogComponent,
      OutputFormula | undefined
    >,
    private readonly service: OutputFormulaService,
  ) {}

  get rangeConventions(): FilenameConvention[] {
    return filterRangeConventions(this.data.filenameConventions);
  }

  get stillsFileSpecifications(): FileSpecification[] {
    return filterStillsSpecs(this.data.fileSpecifications);
  }

  get videosFileSpecifications(): FileSpecification[] {
    return filterVideosSpecs(this.data.fileSpecifications);
  }

  get hasAnySectionOpen(): boolean {
    return this.stillsSectionOpen || this.videosSectionOpen;
  }

  get stillsSectionFilled(): boolean {
    return (
      this.stillsSpecIdControl.value !== null &&
      this.stillsConventionIdControl.value !== null
    );
  }

  get videosSectionFilled(): boolean {
    return (
      this.videosSpecIdControl.value !== null &&
      this.videosConventionIdControl.value !== null
    );
  }

  get canCreate(): boolean {
    if (this.submitting) return false;
    if (!this.nameControl.valid) return false;
    if (!this.hasAnySectionOpen) return false;
    if (this.stillsSectionOpen && !this.stillsSectionFilled) return false;
    if (this.videosSectionOpen && !this.videosSectionFilled) return false;
    return true;
  }

  addStillsSection(): void {
    if (this.stillsSectionOpen) return;
    this.stillsSectionOpen = true;
  }

  addVideosSection(): void {
    if (this.videosSectionOpen) return;
    this.videosSectionOpen = true;
  }

  removeStillsSection(): void {
    this.stillsSectionOpen = false;
    this.stillsSpecIdControl.setValue(null);
    this.stillsConventionIdControl.setValue(null);
  }

  removeVideosSection(): void {
    this.videosSectionOpen = false;
    this.videosSpecIdControl.setValue(null);
    this.videosConventionIdControl.setValue(null);
  }

  create(): void {
    if (!this.canCreate) return;
    const value = this.buildValue();
    if (value === null) return;
    this.submitting = true;
    this.service
      .create(this.data.organizationId, {
        name: this.nameControl.value.trim(),
        value,
        writeReason: { kind: 'initialCreate' },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: OutputFormula) => this.dialogRef.close(result),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(undefined);
  }

  private buildValue(): OutputFormulaValue | null {
    if (this.stillsSectionOpen && this.videosSectionOpen) {
      const stillsSpecId = this.stillsSpecIdControl.value;
      const stillsConventionId = this.stillsConventionIdControl.value;
      const videosSpecId = this.videosSpecIdControl.value;
      const videosConventionId = this.videosConventionIdControl.value;
      if (
        stillsSpecId === null || stillsConventionId === null ||
        videosSpecId === null || videosConventionId === null
      ) return null;
      return {
        kind: 'rangeBoth',
        stills: { specId: stillsSpecId, conventionId: stillsConventionId },
        videos: { specId: videosSpecId, conventionId: videosConventionId },
      };
    }
    if (this.stillsSectionOpen) {
      const specId = this.stillsSpecIdControl.value;
      const conventionId = this.stillsConventionIdControl.value;
      if (specId === null || conventionId === null) return null;
      return { kind: 'rangeStills', stills: { specId, conventionId } };
    }
    if (this.videosSectionOpen) {
      const specId = this.videosSpecIdControl.value;
      const conventionId = this.videosConventionIdControl.value;
      if (specId === null || conventionId === null) return null;
      return { kind: 'rangeVideos', videos: { specId, conventionId } };
    }
    return null;
  }
}
